// contexts/produtos/AdicionalContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as AdicionalService from '../../services/produtos/AdicionalService';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const AdicionalContext = createContext();

export const useAdicional = () => useContext(AdicionalContext);

export const AdicionalProvider = ({ children }) => {
    const [adicionais, setAdicionais] = useState([]);
    const [adicionaisProduto, setAdicionaisProduto] = useState([]);
    const produtoIdSelecionado = useRef(null);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();

    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                const result = await AdicionalService.getAdicionais();
                setAdicionais(result);
            } catch (error) {
                console.error("Erro ao buscar adicionais:", error);
                showMessage('Erro ao buscar adicionais!\n' + error, 'danger');
                return [];
            }
        };

        fetchDataFromAPI();
    }, []);

    const getAdicionaisPorProduto = useCallback((listaAdicionais, produtoId) => {
        if (!produtoId) {
            return [];
        }

        return listaAdicionais.filter(a => a.produtoId?.toString() === produtoId?.toString());
    }, []);

    const fetchAdicionaisProdutos = useCallback((produtoId) => {
        produtoIdSelecionado.current = produtoId;
        const adicionaisFiltrados = getAdicionaisPorProduto(adicionais, produtoId);
        setAdicionaisProduto(adicionaisFiltrados);
        return adicionaisFiltrados;
    }, [adicionais, getAdicionaisPorProduto]);

    useEffect(() => {
        if (produtoIdSelecionado.current) {
            setAdicionaisProduto(getAdicionaisPorProduto(adicionais, produtoIdSelecionado.current));
        }
    }, [adicionais, getAdicionaisPorProduto]);

    const addAdicional = async (adicional) => {
        try {
            const result = await AdicionalService.addAdicional(adicional);
            const adicionalResult = {
                ...result,
                produtoId: result.produtoId || adicional.produtoId
            };

            setAdicionais(prev => [...prev, adicionalResult]);
            setAdicionaisProduto(prev => (
                adicionalResult.produtoId?.toString() === adicional.produtoId?.toString()
                    ? [...prev, adicionalResult]
                    : prev
            ));
            showAlert('Adicional adicionado com sucesso!');
            return adicionalResult;
        } catch (error) {
            console.error("Erro ao adicionar adicional:", error);
            showMessage('Erro ao adicionar adicional!\n' + error, 'danger');
        }
    };

    const updateAdicional = async (adicional) => {
        try {
            const result = await AdicionalService.updateAdicional(adicional);
            setAdicionais(prev => prev.map(a => a.id === result.id ? result : a));
            setAdicionaisProduto(prev => prev.map(a => a.id === result.id ? result : a));
            showAlert('Adicional alterado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao atualizar adicional:", error);
            showMessage('Erro ao atualizar adicional!\n' + error, 'danger');
        }
    };

    const deleteAdicional = async (id) => {
        try {
            await AdicionalService.deleteAdicional(id);
            setAdicionais(prev => prev.filter(a => a.id !== id));
            setAdicionaisProduto(prev => prev.filter(a => a.id !== id));
            showAlert('Adicional excluído com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir adicional:", error);
            showMessage('Erro ao excluir adicional!\n' + error, 'danger');
        }
    };

    return (
        <AdicionalContext.Provider value={{
            adicionais,
            setAdicionais,
            adicionaisProduto,
            setAdicionaisProduto,
            fetchAdicionaisProdutos,
            addAdicional,
            updateAdicional,
            deleteAdicional
        }}>
            {children}
        </AdicionalContext.Provider>
    );
};
